/**
 * Bounce Page Access Control
 * Validates user access to bounce page and manages coupon expiry states
 */

class BouncePageAccessControl {
  constructor() {
    this.timerInterval = null;
    this.isPreviewMode = this.checkPreviewMode();
    this.init();
  }

  checkPreviewMode() {
    // Preview mode requires both backend setting enabled AND URL parameter
    const previewEnabled = window.bouncePreviewEnabled || false;

    if (!previewEnabled) {
      return false;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const previewParam = urlParams.get('preview');

    if (previewParam === 'true') {
      console.log('🔍 Preview mode activated - bypassing access control');
      this.showPreviewBanner();
      return true;
    }

    return false;
  }

  showPreviewBanner() {
    // Add visual indicator that preview mode is active
    const banner = document.createElement('div');
    banner.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: white;
      padding: 10px 20px;
      text-align: center;
      font-weight: 600;
      z-index: 99999;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
      font-size: 14px;
    `;
    banner.innerHTML = '🔍 PREVIEW MODE - Access control and already-viewed logic bypassed';
    document.body.prepend(banner);

    // Adjust body padding to account for banner
    document.body.style.paddingTop = '40px';

    // Append ?preview=true to all internal links
    this.appendPreviewParamToLinks();
  }

  appendPreviewParamToLinks() {
    // Get all anchor tags on the page
    const links = document.querySelectorAll('a[href]');

    links.forEach(link => {
      const href = link.getAttribute('href');

      // Skip if not a valid href
      if (!href || href.startsWith('#') || href.startsWith('javascript:') || href.startsWith('mailto:') || href.startsWith('tel:')) {
        return;
      }

      try {
        // Check if it's an internal link (same origin or relative)
        const isInternal = href.startsWith('/') ||
                          href.startsWith('./') ||
                          href.startsWith('../') ||
                          href.includes(window.location.hostname);

        if (isInternal) {
          // Parse the URL
          let url;
          if (href.startsWith('/')) {
            url = new URL(href, window.location.origin);
          } else if (href.startsWith('.')) {
            url = new URL(href, window.location.href);
          } else {
            url = new URL(href);
          }

          // Add preview parameter if not already present
          if (!url.searchParams.has('preview')) {
            url.searchParams.set('preview', 'true');
            link.setAttribute('href', url.toString());
          }
        }
      } catch (e) {
        // Invalid URL, skip it
        console.warn('Could not parse URL:', href);
      }
    });

    console.log('🔍 Preview mode: Added preview parameter to internal links');
  }

  init() {
    // Prevent execution in Theme Editor to allow page editing
    if (window.Shopify && window.Shopify.designMode) {
      console.log('Theme editor mode detected - skipping access control');
      return;
    }

    // Prevent execution if bounce urgency section was disabled
    if (window.bounceUrgencyDisabled) {
      console.log('Bounce urgency disabled - skipping access control');
      return;
    }

    this.validateAccess();
    this.initTimer();
    this.listenForCouponEvents();
  }

  validateAccess() {
    // In preview mode, skip access validation
    if (this.isPreviewMode) {
      console.log('Preview mode: Skipping access validation');
      this.checkOfferExpiry();
      return;
    }

    const bouncePageAccess = this.getCookie('bounce_page_access');

    // Check if cookie exists
    if (!bouncePageAccess) {
      // No access cookie - redirect to homepage
      // console.log('No bounce page access cookie - redirecting to homepage');
      window.location.href = '/';
      return;
    }

    // Validate the timestamp
    const accessExpiry = parseInt(bouncePageAccess);

    // Check if it's a valid timestamp
    if (isNaN(accessExpiry)) {
      // console.log('Invalid bounce page access timestamp - redirecting to homepage');
      window.location.href = '/';
      return;
    }

    // Check if the access has expired
    if (Date.now() >= accessExpiry) {
      // console.log('Bounce page access has expired - redirecting to homepage');
      window.location.href = '/';
      return;
    }

    // console.log('Bounce page access valid until:', new Date(accessExpiry).toISOString());

    // Has valid access - check if offer is expired
    this.checkOfferExpiry();
  }

  checkOfferExpiry() {
    let expiryTimestamp = null;

    // Read expiry from localStorage (cookies removed)
    try {
      const couponData = localStorage.getItem('bounce_coupon_data');
      if (couponData) {
        const data = JSON.parse(couponData);
        expiryTimestamp = data.expiryTime;
      }
    } catch (e) {
      console.warn('Could not read expiry from localStorage');
    }

    if (!expiryTimestamp) {
      // No expiry set - coupon hasn't been generated yet
      // Don't set a timer, wait for coupon generation
      // console.log('Waiting for coupon generation...');
      return;
    }

    // Validate timestamp format and value
    if (!this.isValidTimestamp(expiryTimestamp.toString())) {
      console.error('Invalid expiry timestamp detected:', expiryTimestamp);
      // Show expired state - don't reset timer
      this.showExpiredOffer();
      return;
    }

    const now = Date.now();
    const expiry = parseInt(expiryTimestamp);

    if (now < expiry) {
      // Offer still active
      this.showActiveOffer(expiry);
    } else {
      // Offer expired (but page still accessible)
      this.showExpiredOffer();
    }
  }

  showActiveOffer(expiryTimestamp) {
    // console.log('Coupon active - starting countdown');
    // Timer will be handled by initTimer()
  }

  showExpiredOffer() {
    // console.log('Coupon expired - showing expired state');

    // Update UI to show expired state
    const timerDisplay = document.querySelector('.bounce-timer__display');
    const statusMessage = document.querySelector('.bounce-coupon__status');
    const couponSection = document.querySelector('.bounce-coupon');

    if (timerDisplay) {
      timerDisplay.textContent = '00:00:00';
      timerDisplay.classList.add('expired');
    }

    if (statusMessage) {
      statusMessage.textContent = '⚠️ Dein Gutschein ist abgelaufen';
      statusMessage.classList.add('expired');
      statusMessage.style.display = 'block';
    }

    // Disable copy button
    const copyButton = document.getElementById('bounceCopyCoupon');
    if (copyButton) {
      copyButton.disabled = true;
      const buttonText = copyButton.querySelector('.bounce-coupon__copy-text');
      if (buttonText) {
        buttonText.textContent = 'Abgelaufen';
      }
    }

    // Add expired class to coupon section
    if (couponSection) {
      couponSection.classList.add('expired');
    }
  }

  initTimer() {
    let expiryTimestamp = null;

    // Read expiry from localStorage (cookies removed)
    try {
      const couponData = localStorage.getItem('bounce_coupon_data');
      if (couponData) {
        const data = JSON.parse(couponData);
        expiryTimestamp = data.expiryTime;
      }
    } catch (e) {
      console.warn('Could not read expiry from localStorage');
    }

    if (!expiryTimestamp) return;

    const expiry = parseInt(expiryTimestamp);
    const now = Date.now();

    if (now >= expiry) {
      // Already expired
      this.showExpiredOffer();
      return;
    }

    // Start countdown
    this.updateTimer(expiry);
    this.timerInterval = setInterval(() => this.updateTimer(expiry), 1000);
  }

  updateTimer(expiryTimestamp) {
    const now = Date.now();
    const remaining = expiryTimestamp - now;

    if (remaining <= 0) {
      clearInterval(this.timerInterval);
      this.showExpiredOffer();
      return;
    }

    // Calculate hours, minutes, seconds
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);

    const timerDisplay = document.querySelector('.bounce-timer__display');
    if (timerDisplay) {
      timerDisplay.textContent = `${this.pad(hours)}:${this.pad(minutes)}:${this.pad(seconds)}`;
    }
  }

  pad(num) {
    return num.toString().padStart(2, '0');
  }

  isValidTimestamp(timestamp) {
    // Check if timestamp is a valid number
    if (!timestamp || typeof timestamp !== 'string') return false;

    const parsed = parseInt(timestamp);

    // Check if parsing resulted in NaN
    if (isNaN(parsed)) return false;

    // Check if it's a reasonable timestamp (13 digits for milliseconds)
    if (parsed.toString().length !== 13) return false;

    // Check if timestamp is not in the past (with 1 minute grace period)
    const oneMinuteAgo = Date.now() - (60 * 1000);
    if (parsed < oneMinuteAgo) return false;

    // Check if timestamp is not too far in the future (max 24 hours for offer expiry)
    const maxFuture = Date.now() + (24 * 60 * 60 * 1000);
    if (parsed > maxFuture) return false;

    // Check if it's a valid date
    const date = new Date(parsed);
    if (date.toString() === 'Invalid Date') return false;

    return true;
  }

  getCookie(name) {
    const nameEQ = name + '=';
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length);
    }
    return null;
  }

  listenForCouponEvents() {
    // Listen for coupon generation failures
    document.addEventListener('couponGenerationFailed', () => {
      // console.log('Coupon generation failed - showing error state');
      this.showExpiredOffer();
    });

    // Listen for successful coupon generation to start timer
    document.addEventListener('couponGenerated', (event) => {
      // console.log('Coupon generated - starting timer');
      const { expiryTime } = event.detail;

      // Clear any existing timer
      if (this.timerInterval) {
        clearInterval(this.timerInterval);
      }

      // Start new timer with the expiry time
      this.updateTimer(expiryTime);
      this.timerInterval = setInterval(() => this.updateTimer(expiryTime), 1000);
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new BouncePageAccessControl();
  });
} else {
  new BouncePageAccessControl();
}
